import CustomerProfile from '@/domain/CustomerProfile'
import { adminType as ADMIN_TYPE, programType as PROGRAM_TYPE } from '@/domain/MyAccount'
import { loyaltyApi } from '@/infrastructure/Configuration'
import { isDesktop } from '@/infrastructure/Devices'

const defaultGetConfig = {
  headers: { 'Content-Type': 'application/json' },
  data: {}
}
const config = {
  headers: {
    'X-SOURCE-OF-BUSINESS': isDesktop() ? 'desktop' : 'mobile'
  }
}

export default class LoyaltyService {
  constructor(httpClient) {
    this.httpClient = httpClient
  }

  async getHotelDirectory(params) {
    const url = `${loyaltyApi}/admin/hotels`
    const response = await this.httpClient.get(url, { params: params })
    return response.data
  }

  async getCountries() {
    const url = `${loyaltyApi}/admin/hotels/countries`
    const response = await this.httpClient.get(url)
    return response.data.countries
  }

  async getStatesByCountry(countryCode) {
    const url = `${loyaltyApi}/admin/hotels/countries/${countryCode}/states`
    const response = await this.httpClient.get(url)
    return response.data.states
  }

  async getCountriesByMemberType(memberType) {
    const url = `${loyaltyApi}/sign-up/countries?memberType=${memberType}`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data.countries
  }

  async getCitiesByCountryOrState(location) {
    const url = `${loyaltyApi}/admin/hotels/countries/${location.countryCode}/cities`
    const params = {
      stateCode: location.stateCode
    }
    const response = await this.httpClient.get(url, { params: params })
    return response.data.cities
  }

  async getAirlines() {
    const url = `${loyaltyApi}/admin/airlines`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data.airlines
  }

  async validateIata(iataCode) {
    const url = `${loyaltyApi}/validate-iata/${iataCode}`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data.travelAgencyAddress
  }

  async getProfile() {
    const url = `${loyaltyApi}/me/profile`
    const response = await this.httpClient.get(url, defaultGetConfig)
    const customerProfile = response.data.customerProfile

    return CustomerProfile.fromJSON(customerProfile)
  }

  async getCorporateActiveAdmins(loyaltyNumberCorporate) {
    const url = `${loyaltyApi}/corporate/${loyaltyNumberCorporate}/admins`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data.members.active
  }

  async getMe() {
    const url = `${loyaltyApi}/me`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data.customer
  }

  async getPreferences() {
    const url = `${loyaltyApi}/me/preferences`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data
  }

  async getPromocodes(promoCode, path = 'privateArea') {
    const params = {
      promoCode,
      path
    }
    const queryParams = global.Utils.stringifyQueryParams(params)
    const url = `${loyaltyApi}/me/vouchers?${queryParams}`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data
  }

  async getSummary() {
    const url = `${loyaltyApi}/me/summary`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data.customerAccount
  }

  async savePreferences(data) {
    const url = `${loyaltyApi}/update-preferences`
    const response = await this.httpClient.put(url, data, config)
    return response.data
  }

  async updateProfile(data) {
    const url = `${loyaltyApi}/update-profile`

    const response = await this.httpClient.put(url, data)
    return response.data
  }

  async updateEmail(customerEmail) {
    const url = `${loyaltyApi}/update-email`
    const response = await this.httpClient.put(url, customerEmail)
    return response.data
  }

  async signupPassword(customer) {
    const url = `${loyaltyApi}/sign-up-with-password`
    const response = await this.httpClient.post(url, customer, config)
    return response.data
  }

  async signUpB2b(businessUser) {
    const url = `${loyaltyApi}/sign-up-b2b`

    const { data } = await this.httpClient.post(url, businessUser, config)
    return data
  }

  async getb2bProfileFields(memberType) {
    const url = `${loyaltyApi}/catalog-b2b/${memberType}/profile`

    const { data } = await this.httpClient.get(url, defaultGetConfig)
    return data
  }

  async getb2cProfileFields() {
    const url = `${loyaltyApi}/catalog-b2c/profile`
    const { data } = await this.httpClient.get(url, defaultGetConfig)
    return data
  }

  async getRegisteredOffers() {
    const url = `${loyaltyApi}/offers/registered-by-user`
    const { data } = await this.httpClient.get(url, defaultGetConfig)
    return data
  }

  async transferPoints(data) {
    const url = `${loyaltyApi}/transfer/points`

    const response = await this.httpClient.post(url, {
      pointTransfer: {
        ...data.pointTransfer,
        loyaltyNumberTo: data.pointTransfer.loyaltyNumberTo.toString()
      }
    })
    return response.data
  }

  async getManageMembers({ corporateId, name, page, limit, adminType }) {
    const endpointType = adminType === ADMIN_TYPE.CORPORATE ? 'corporate' : 'company'
    const programType = adminType === ADMIN_TYPE.R4U ? PROGRAM_TYPE.R4U : PROGRAM_TYPE.ALL
    const params = []
    if (name) {
      params.push(`name=${name}`)
    }
    if (page || page === 0) {
      params.push(`offset=${page}`)
    }
    if (limit) {
      params.push(`limit=${limit}`)
    }
    if (adminType !== ADMIN_TYPE.CORPORATE) {
      params.push(`programType=${programType}`)
    }
    const queryString = params.length ? `?${params.join('&')}` : ''

    const url = `${loyaltyApi}/${endpointType}/${corporateId}${queryString}`
    const { data } = await this.httpClient.get(url, defaultGetConfig)
    return data
  }

  async deactivateMember(corporateId, loyaltyNumber) {
    const url = `${loyaltyApi}/company/${corporateId}/member/${loyaltyNumber}/deactivate`
    const { data } = await this.httpClient.put(url, defaultGetConfig)
    return data
  }

  async generateEnrollentLinks(corporateId, emails, selectedProgram, r4uType) {
    let program = null
    const requestBody = {
      emails
    }
    switch (selectedProgram) {
      case PROGRAM_TYPE.RR:
        program = 'corporate'
        break
      case PROGRAM_TYPE.R4U:
        program = 'r4u'
        requestBody.type = r4uType
        break
      default:
        break
    }
    const url = `${loyaltyApi}/${program}/${corporateId}/member/enroll`
    const { data } = await this.httpClient.post(url, requestBody)
    return data
  }

  async getUserDna() {
    const url = `${loyaltyApi}/me/dna`
    const { data } = await this.httpClient.get(url, defaultGetConfig)
    return data
  }

  async getTransactions({ type, filter, page, limit, dateFrom, dateTo, accountType }) {
    const limitToDisplay = limit
    const typeParam = type !== undefined ? type : ''
    const filterParam = filter !== undefined ? filter : ''
    const dateFromParam = dateFrom !== undefined ? dateFrom : ''
    const dateToParam = dateTo !== undefined ? dateTo : ''
    const accountTypeParam = accountType !== undefined ? accountType : ''

    const url = `${loyaltyApi}/me/v2/transactions?offset=${page !== undefined ? page : '0'}&limit=${
      limitToDisplay !== undefined ? limitToDisplay : ''
    }${typeParam !== '' ? '&type=' + typeParam : ''}${
      filterParam !== '' ? '&filter=' + filterParam : ''
    }${dateFromParam !== '' ? '&dateFrom=' + dateFromParam : ''}${
      dateToParam !== '' ? '&dateTo=' + dateToParam : ''
    }${accountTypeParam !== '' ? '&accountType=' + accountTypeParam : ''}`

    try {
      const { data } = await this.httpClient.get(url, defaultGetConfig)
      return { count: data.total, transactions: data.transactions }
    } catch (error) {
      return { count: 0, transactions: [] }
    }
  }

  async updateSubscription(customer) {
    const url = `${loyaltyApi}/newsletter/subscribe`
    const response = await this.httpClient.post(url, customer)
    return response.data
  }

  async claimPoints(bookingId) {
    const url = `${loyaltyApi}/points/claim`
    const response = await this.httpClient.post(url, bookingId)
    return response.data
  }

  async removeTrustedDevices() {
    const url = `${loyaltyApi}/mfa/trusted-devices`
    const response = await this.httpClient.put(url)
    return response.data
  }

  async requestActivateMfa(data) {
    const url = `${loyaltyApi}/mfa/request-activate`

    const response = await this.httpClient.put(url, data)
    return response.data
  }

  async appleTree() {
    const url = `${loyaltyApi}/me/apple-tree-token-b2b`
    const response = await this.httpClient.get(url, defaultGetConfig)
    return response.data
  }
}
